/* ──────────────────────────────────────────────────────────────────────────
   Links in a to-do's notes, read as chips (Jay, 2026-10-08): a Google
   Sheet, Doc, Slides, Form or Drive link shows as what it is, and any other
   link shows its site. Pure, so the page and tests read notes the same way.
   ────────────────────────────────────────────────────────────────────────── */

export type LinkKind = "sheet" | "doc" | "slides" | "form" | "folder" | "drive" | "web";

export type NoteLink = { url: string; kind: LinkKind; label: string };

export type NotePart = { text: string } | { link: NoteLink };

const URL_RE = /\bhttps?:\/\/[^\s<>"')\]]+/gi;

export function classify(url: string): NoteLink {
  let host = "";
  let path = "";
  try {
    const u = new URL(url);
    host = u.hostname.replace(/^www\./, "").toLowerCase();
    path = u.pathname;
  } catch {
    return { url, kind: "web", label: url };
  }
  if (host === "docs.google.com") {
    if (path.startsWith("/spreadsheets")) return { url, kind: "sheet", label: "Google Sheet" };
    if (path.startsWith("/document")) return { url, kind: "doc", label: "Google Doc" };
    if (path.startsWith("/presentation")) return { url, kind: "slides", label: "Google Slides" };
    if (path.startsWith("/forms")) return { url, kind: "form", label: "Google Form" };
  }
  if (host === "forms.gle") return { url, kind: "form", label: "Google Form" };
  if (host === "drive.google.com") {
    if (path.includes("/folders/")) return { url, kind: "folder", label: "Drive folder" };
    return { url, kind: "drive", label: "Drive file" };
  }
  return { url, kind: "web", label: host };
}

/** The notes as plain text and links, in order, trailing punctuation left out of a link. */
export function readNotes(notes: string): NotePart[] {
  const parts: NotePart[] = [];
  let at = 0;
  for (const match of notes.matchAll(URL_RE)) {
    let url = match[0];
    const trailing = /[.,;:!?]+$/.exec(url)?.[0] ?? "";
    url = url.slice(0, url.length - trailing.length);
    const start = match.index!;
    if (start > at) parts.push({ text: notes.slice(at, start) });
    parts.push({ link: classify(url) });
    at = start + url.length;
  }
  if (at < notes.length) parts.push({ text: notes.slice(at) });
  return parts;
}

export function linksIn(notes: string): NoteLink[] {
  return readNotes(notes).flatMap((part) => ("link" in part ? [part.link] : []));
}
